import React, { useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CaretLeft, CaretRight, ArrowSquareOut, Pencil, Trash, Eye } from '@phosphor-icons/react';

/**
 * Full-screen lightbox viewer for admin content review.
 * Props:
 *   open: bool
 *   items: array (each item must have id, title, thumbnail_url, source_url, source_name, content_type, category, tier, views, duration?)
 *   index: number (current item index)
 *   onClose: () => void
 *   onChangeIndex: (newIndex) => void
 *   onEdit: (item) => void
 *   onDelete: (item) => void
 */
const AdminGalleryViewer = ({ open, items, index, onClose, onChangeIndex, onEdit, onDelete }) => {
  const item = items?.[index];
  const [imageError, setImageError] = useState(false);

  useEffect(() => { setImageError(false); }, [index, item?.id]);

  const next = useCallback(() => {
    if (!items?.length) return;
    onChangeIndex((index + 1) % items.length);
  }, [index, items, onChangeIndex]);

  const prev = useCallback(() => {
    if (!items?.length) return;
    onChangeIndex((index - 1 + items.length) % items.length);
  }, [index, items, onChangeIndex]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Delete') {
        if (window.confirm('Delete this item?')) onDelete?.(item);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, next, prev, onClose, onDelete, item]);

  if (!open || !item) return null;

  const isVideo = item.content_type === 'video';
  const previewUrl = item.thumbnail_url;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm"
        onClick={onClose}
        data-testid="admin-gallery-viewer"
      >
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 px-4 py-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent z-10"
             onClick={e => e.stopPropagation()}>
          <div className="text-slate-300 text-sm">
            <span className="text-white font-bold">{index + 1}</span>
            <span className="text-slate-500"> / {items.length}</span>
            <span className="ml-3 text-slate-500">·</span>
            <span className="ml-3">{item.source_name}</span>
            <span className="ml-3 px-2 py-0.5 bg-slate-800 rounded-full text-xs">{item.category}</span>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              item.tier === 'free' ? 'bg-green-500/20 text-green-400' :
              item.tier === 'basic' ? 'bg-blue-500/20 text-blue-400' :
              'bg-amber-500/20 text-amber-400'
            }`}>{item.tier}</span>
          </div>
          <button onClick={onClose}
            className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            data-testid="gallery-close-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Prev / Next */}
        {items.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full transition-colors z-10"
              data-testid="gallery-prev-btn">
              <CaretLeft weight="bold" className="w-7 h-7" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full transition-colors z-10"
              data-testid="gallery-next-btn">
              <CaretRight weight="bold" className="w-7 h-7" />
            </button>
          </>
        )}

        {/* Media */}
        <motion.div
          key={item.id}
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className="relative max-w-[90vw] max-h-[80vh] flex items-center justify-center"
          onClick={e => e.stopPropagation()}>
          {imageError ? (
            <div className="w-[60vw] h-[60vh] flex items-center justify-center bg-slate-900 rounded-2xl text-slate-500">
              <div className="text-center">
                <Eye className="w-16 h-16 mx-auto mb-3 opacity-50" />
                <p>Preview unavailable</p>
                <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                   className="mt-3 inline-block text-blue-400 hover:underline text-sm">
                  Open source ↗
                </a>
              </div>
            </div>
          ) : (
            <img
              src={previewUrl}
              alt={item.title}
              onError={() => setImageError(true)}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
              data-testid="gallery-image"
            />
          )}
          {isVideo && (
            <span className="absolute top-3 left-3 px-3 py-1 bg-black/70 text-white text-xs font-bold rounded-full backdrop-blur-md flex items-center gap-1">
              VIDEO {item.duration && `· ${item.duration}`}
            </span>
          )}
        </motion.div>

        {/* Bottom bar: title + actions */}
        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/90 to-transparent z-10"
             onClick={e => e.stopPropagation()}>
          <div className="max-w-5xl mx-auto flex items-end justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-lg truncate" data-testid="gallery-title">{item.title}</h3>
              <p className="text-slate-500 text-xs mt-0.5">
                {item.content_type}
                {item.views > 0 && ` · ${(item.views / 1000).toFixed(1)}K views`}
                {item.tags?.length > 0 && ` · ${item.tags.slice(0, 4).join(', ')}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                 className="px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-lg text-sm flex items-center gap-2"
                 data-testid="gallery-open-source">
                <ArrowSquareOut className="w-4 h-4" /> Source
              </a>
              {onEdit && (
                <button onClick={() => onEdit(item)}
                  className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/40 text-blue-300 rounded-lg text-sm flex items-center gap-2"
                  data-testid="gallery-edit">
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              )}
              {onDelete && (
                <button onClick={() => { if (window.confirm('Delete this item?')) onDelete(item); }}
                  className="px-3 py-2 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded-lg text-sm flex items-center gap-2"
                  data-testid="gallery-delete">
                  <Trash className="w-4 h-4" /> Delete
                </button>
              )}
            </div>
          </div>
          <p className="text-center text-slate-600 text-xs mt-3">
            ← → navigate · <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">Esc</kbd> close · <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">Del</kbd> delete
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminGalleryViewer;
